import { FileDto } from '@tallo/file-manager';
import { TeamMemberDto } from '@tallo/investor/company';
import {
  AccessibilityDto,
  AmenitiesDto,
  ParkingDto,
  PetPolicyDto,
  PropertyLeaseConditionsDto,
  PropertyLocationDto,
  PropertyRenterRequirementsDto,
  PropertyStatus,
} from '@tallo/property';

import { SaveLocationDto } from './save-location.dto.interface';

/** A building: the record its units hang off, with their shared address, manager and portfolio */
export interface BuildingDto {
  id: string;
  name: string;
  /** The URL of its cover photo, which is the first of its photos; null until someone adds one */
  coverImage: string | null;
  /** The building's photos in order; the first is the cover */
  images: FileDto[];
  yearBuilt: number | null;
  /** How to get in and where the office is; Tallo uses it in tour confirmations */
  accessNotes: string | null;
  /** `apartmentNumber` is always null: a unit's number is the unit's own */
  location: PropertyLocationDto;
  /** The building's row; only its building-level keys reach the units. Null until filled in */
  amenities: AmenitiesDto | null;
  parking: ParkingDto | null;
  accessibility: AccessibilityDto | null;
  /** What a new unit starts with; a unit answers for itself once it exists. Null until set */
  renterRequirements: PropertyRenterRequirementsDto | null;
  petPolicy: PetPolicyDto | null;
  /** The lease terms, deposits and fees a new unit starts with; its rent and move-in date are never the building's */
  leaseConditions: PropertyLeaseConditionsDto | null;
  /**
   * Per default, the units whose own copy no longer matches the building's; only on a single building's read.
   * The lease conditions row is two cards, each compared on its own columns
   */
  defaultsDrift?: {
    renterRequirements: string[];
    petPolicy: string[];
    leaseTerm: string[];
    depositsAndFees: string[];
    tourTypes: string[];
    /** The renter requirements row on its screening sensitivity alone */
    screeningSensitivity: string[];
  };
  /** The tours a new unit offers to start with; null until the building sets them */
  tourTypes: BuildingTourTypesDto | null;
  /** Null while the building is in no portfolio */
  portfolio: { id: string; name: string } | null;
  /** Null only when the manager's seat has been removed */
  manager: TeamMemberDto | null;
  unitCount: number;
  /** The live units by status; a status no unit is in is left out */
  unitsByStatus: Partial<Record<PropertyStatus, number>>;
  createdAt: string;
}

/** POST building: filed straight into a portfolio when one is given */
export interface CreateBuildingDto {
  /** Up to 120 characters; left blank, the building goes by its street address */
  name?: string;
  /** `apartmentNumber` is ignored */
  location: SaveLocationDto;
  portfolioId?: string;
}

/** PATCH building/:id: the building's own fields; the shared sections have their own calls */
export interface UpdateBuildingDto {
  /** Up to 120 characters; cleared, the building goes back to its street address */
  name?: string;
  yearBuilt?: number | null;
  /** Up to 4000 characters */
  accessNotes?: string | null;
}

/** The sections a building shares with every unit, each saved with the unit forms' own payloads */
export type BuildingSection = 'location' | 'amenities' | 'parking' | 'accessibility';

/** The defaults a new unit copies from its building and then owns; saved with the unit forms' payloads too */
export type UnitDefaultSection = 'renterRequirements' | 'petPolicy' | 'leaseConditions' | 'tourTypes';

/** The tours a new unit of the building offers to start with; at least one */
export interface BuildingTourTypesDto {
  allowsInPersonTours: boolean;
  allowsVirtualTours: boolean;
  allowsSelfGuidedTours: boolean;
}

/** The units' leasing over the last 30 days, summed, and when their next booked tour starts */
export interface BuildingStatisticsDto {
  leads: number;
  qualifiedLeads: number;
  showingsTotal: number;
  showingsBooked: number;
  messagesSent: number;
  followUpsSent: number;
  applicationsSent: number;
  applicationsCompleted: number;
  nextShowingAt: string | null;
}

export const buildingErrorCodes = {
  /** Deleting a building that still has units; the response names `unitCount` */
  buildingHasUnits: 'BUILDING_HAS_UNITS',
  /** Adding a unit with a number another unit of the building has; the response names `unitNumber` */
  unitNumberTaken: 'UNIT_NUMBER_TAKEN',
  /** Attaching a home that is not an apartment */
  unitNotApartment: 'UNIT_NOT_APARTMENT',
  /** Attaching a home that is a unit of a building already */
  alreadyInBuilding: 'ALREADY_IN_BUILDING',
} as const;

/** One standing apartment to file into a building, under the number it goes by from then on */
export interface AttachUnitDto {
  propertyId: string;
  unitNumber: string;
}
